//
// useQnsResolution — calls /api/qns/* BFF route (no api-key in browser).
//
// Cache strategy (multi-layer, all transparent to consumers):
//   1. localStorage — persists across page reloads, tabs, browser restarts.
//      Cached entries (7 day TTL) are read SYNCHRONOUSLY at hook init, so
//      the `.qie` name renders on first paint with no network call and no
//      "flash of hex address".
//   2. React Query in-memory cache — dedupes simultaneous lookups on a
//      single page (e.g., 50 rows of a tx list all referencing same address).
//   3. Server-side cache in nextjs/utils/qnsResolver.ts (24h success TTL,
//      5min error TTL) — protects the QIE upstream from repeat hits.
//   4. HTTP Cache-Control on the BFF response — backup browser cache.
//
// Once an address is resolved successfully, the user won't see another
// network request for it for 7 days.
//
import { useQueries, useQuery } from '@tanstack/react-query';
import React from 'react';

import { getEnvValue } from 'configs/app/utils';

export interface QnsDomain {
  domainName: string;
  domainType: string;
}

export interface QnsResolution {
  address: string;
  primaryDomain: string | null;
  allDomains: Array<QnsDomain>;
  totalDomains: number;
}

const QNS_ENABLED = getEnvValue('NEXT_PUBLIC_QNS_ENABLED') === 'true';

const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const LOCAL_STORAGE_KEY = 'qns-cache-v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ---------------------------------------------------------------------------
// Persistent browser cache (localStorage-backed, in-memory mirror)
// ---------------------------------------------------------------------------

interface PersistedEntry {
  // primaryDomain can be null = "we resolved this address, it has no .qie domain"
  // We cache nulls too, so we don't re-fetch for addresses we already know have no name.
  primaryDomain: string | null;
  expiresAt: number;
}

let memoryCache: Record<string, PersistedEntry> = {};
let cacheLoaded = false;

function loadCache(): void {
  if (cacheLoaded || typeof window === 'undefined') return;
  cacheLoaded = true;
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, PersistedEntry>;
    const now = Date.now();
    // Drop expired entries on load to keep the cache tidy.
    memoryCache = Object.fromEntries(
      Object.entries(parsed).filter(([ , v ]) =>
        v && typeof v.expiresAt === 'number' && v.expiresAt > now,
      ),
    );
  } catch {
    // Corrupted JSON or storage disabled — start fresh, never throw.
    memoryCache = {};
  }
}

function persistCache(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(memoryCache));
  } catch { /* Quota exceeded or storage disabled — in-memory cache still works. */ }
}

function getCached(address: string): PersistedEntry | undefined {
  loadCache();
  const entry = memoryCache[address];
  if (!entry) return undefined;
  if (entry.expiresAt < Date.now()) {
    delete memoryCache[address];
    return undefined;
  }
  return entry;
}

function setCached(address: string, primaryDomain: string | null): void {
  loadCache();
  memoryCache[address] = { primaryDomain, expiresAt: Date.now() + CACHE_TTL_MS };
  persistCache();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidAddress(address: string | undefined | null): address is string {
  return typeof address === 'string' && ADDRESS_RE.test(address);
}

function normalize(address: string): string {
  return address.toLowerCase();
}

async function fetchOne(address: string, signal?: AbortSignal): Promise<QnsResolution> {
  const empty: QnsResolution = { address, primaryDomain: null, allDomains: [], totalDomains: 0 };
  try {
    const res = await fetch(`/api/qns/resolve?address=${ encodeURIComponent(address) }`, { signal });
    if (!res.ok) return empty;
    const data = await res.json() as Partial<QnsResolution>;
    return {
      address,
      primaryDomain: data.primaryDomain ?? null,
      allDomains: Array.isArray(data.allDomains) ? data.allDomains : [],
      totalDomains: typeof data.totalDomains === 'number' ? data.totalDomains : 0,
    };
  } catch {
    return empty;
  }
}

// queryFn used by all hooks — writes to localStorage on success so that
// repeated visits (and other tabs) instantly get the cached value.
async function queryFnWithPersistence(address: string, signal?: AbortSignal): Promise<QnsResolution> {
  const result = await fetchOne(address, signal);
  setCached(address, result.primaryDomain);
  return result;
}

// Build the initialData hint for React Query so cached values render
// SYNCHRONOUSLY on first paint, with no network round-trip.
function buildInitialData(address: string): { data: QnsResolution; updatedAt: number } | undefined {
  const cached = getCached(address);
  if (!cached) return undefined;
  return {
    data: { address, primaryDomain: cached.primaryDomain, allDomains: [], totalDomains: 0 },
    // Tell React Query this data is `CACHE_TTL_MS - timeRemaining` old,
    // so it's considered fresh as long as the cache entry hasn't expired.
    updatedAt: cached.expiresAt - CACHE_TTL_MS,
  };
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

interface UseQnsResolutionOptions {
  enabled?: boolean;
}

/**
 * Resolve a single address to its primary `.qie` name. Returns null when:
 *   - the feature is disabled,
 *   - the address is invalid,
 *   - the address has no `.qie` domains,
 *   - the lookup failed.
 *
 * Safe to call unconditionally inside any component.
 * Once a successful (or empty) resolution is cached, no network call is
 * made for that address for 7 days, regardless of how many components or
 * tabs are rendering it.
 */
export function useQnsResolution(
  address: string | undefined | null,
  options: UseQnsResolutionOptions = {},
): string | null {
  const enabled =
    QNS_ENABLED &&
    options.enabled !== false &&
    isValidAddress(address);

  const normalized = enabled ? normalize(address as string) : '';
  const initial = enabled ? buildInitialData(normalized) : undefined;

  const query = useQuery<QnsResolution>({
    queryKey: [ 'qns', normalized ],
    queryFn: ({ signal }) => queryFnWithPersistence(normalized, signal),
    enabled,
    initialData: initial?.data,
    initialDataUpdatedAt: initial?.updatedAt,
    staleTime: CACHE_TTL_MS,
    gcTime: CACHE_TTL_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  return query.data?.primaryDomain ?? null;
}

/**
 * Same as useQnsResolution, but returns the full resolution object
 * (useful for an address-page header that shows "X owns 3 domains").
 */
export function useQnsResolutionFull(
  address: string | undefined | null,
  options: UseQnsResolutionOptions = {},
): QnsResolution | null {
  const enabled =
    QNS_ENABLED &&
    options.enabled !== false &&
    isValidAddress(address);

  const normalized = enabled ? normalize(address as string) : '';
  const initial = enabled ? buildInitialData(normalized) : undefined;

  const query = useQuery<QnsResolution>({
    queryKey: [ 'qns', normalized ],
    queryFn: ({ signal }) => queryFnWithPersistence(normalized, signal),
    enabled,
    initialData: initial?.data,
    initialDataUpdatedAt: initial?.updatedAt,
    staleTime: CACHE_TTL_MS,
    gcTime: CACHE_TTL_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  return query.data ?? null;
}

/**
 * Resolve many addresses at once (lists / tables). Returns a map
 * keyed by lowercased address. Already-cached addresses don't hit
 * the network — only un-cached ones do, in parallel.
 */
export function useQnsResolutionBatch(
  addresses: Array<string | undefined | null>,
): Record<string, string> {
  const unique = React.useMemo(() => {
    if (!QNS_ENABLED) return [];
    const set = new Set<string>();
    for (const a of addresses) {
      if (isValidAddress(a)) set.add(normalize(a));
    }
    return Array.from(set);
  }, [ addresses ]);

  const results = useQueries({
    queries: unique.map(addr => {
      const initial = buildInitialData(addr);
      return {
        queryKey: [ 'qns', addr ],
        queryFn: ({ signal }: { signal?: AbortSignal }) => queryFnWithPersistence(addr, signal),
        enabled: QNS_ENABLED,
        initialData: initial?.data,
        initialDataUpdatedAt: initial?.updatedAt,
        staleTime: CACHE_TTL_MS,
        gcTime: CACHE_TTL_MS,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 1,
      };
    }),
  });

  return React.useMemo(() => {
    const map: Record<string, string> = {};
    results.forEach((r, i) => {
      const name = r.data?.primaryDomain;
      if (name) map[unique[i]] = name;
    });
    return map;
  }, [ results, unique ]);
}